import { getApiBaseUrl } from "src/config/Endpoints";
import i18n from "src/i18n/config";
import { getApiLangFromI18n, normalizeApiLang } from "src/utils/apiLang";
import { buildAuthHeaders, handleJsonResponse } from "./HttpUtils";

const SOCIAL_LISTENING_URL = `${getApiBaseUrl()}/social-listening`;

export type SocialListeningRunType = "brand" | "competitive";
export type SocialListeningRunStatus = "pending" | "completed" | "partial" | "failed";
export type SocialListeningCompanyStatus = "pending" | "completed" | "failed";
export type SocialListeningCompanyRole = "primary" | "competitor";

/** One brand to analyse. Only `company_name` is required. */
export interface SocialListeningCompanyInput {
  company_name: string;
  facebook_handle?: string;
  instagram_handle?: string;
  tiktok_brand?: string;
  reddit_additional_subreddits?: string[];
}

interface SocialListeningTenantUser {
  user_id: number;
  tenant_id: number;
}

export type SocialListeningBrandAnalysisPayload = SocialListeningTenantUser &
  SocialListeningCompanyInput;

export interface SocialListeningCompetitiveAnalysisPayload extends SocialListeningTenantUser {
  /** The user's own brand first; 2–5 companies. */
  companies: SocialListeningCompanyInput[];
}

export interface SocialListeningStartResponse {
  status: boolean;
  message: string;
  run_id: number;
}

export interface SocialListeningUser {
  user_id: number;
  name: string;
}

/** One row per run in the analyses list. Sentiment fields are the primary company's and `null` until completed. */
export interface SocialListeningRun {
  run_id: number;
  analysis_type: SocialListeningRunType;
  analyses_by: string;
  created_by: SocialListeningUser | null;
  status: SocialListeningRunStatus;
  company_name: string;
  competitors: string[];
  request_id: string | null;
  data_quality_score: number | null;
  sources_analyzed: string[] | null;
  total_count: number | null;
  positive_percentage: number | null;
  neutral_percentage: number | null;
  negative_percentage: number | null;
  created_at: string;
  completed_at: string | null;
}

export interface SocialListeningAnalysisWindow {
  requested_label: string | null;
  collection_mode?: string | null;
  enforced?: boolean | null;
  observed_start: string | null;
  observed_end: string | null;
}

export interface SocialListeningReportHeader {
  request_id: string | null;
  run_timestamp: string | null;
  primary_company: string;
  competitors: string[];
  analysis_window: SocialListeningAnalysisWindow | null;
  requested_by: SocialListeningUser | null;
}

export interface SocialListeningCompany {
  analysis_id: number;
  company_name: string;
  role: SocialListeningCompanyRole;
  status: SocialListeningCompanyStatus;
  created_at: string | null;
  completed_at: string | null;
  /** The agent's per-company result. Its shape is owned by the agent, so it is read defensively. */
  report: Record<string, unknown> | null;
}

export interface SocialListeningRunDetails {
  run_id: number;
  analysis_type: SocialListeningRunType;
  analyses_by: string;
  request_id: string | null;
  status: SocialListeningRunStatus;
  created_by: SocialListeningUser | null;
  created_at: string;
  header: SocialListeningReportHeader;
  companies: SocialListeningCompany[];
  /** Cross-brand tables, pre-merged by the backend; also present for brand runs. */
  comparison: Record<string, unknown> | null;
  competitive_batch: unknown;
}

interface SocialListeningListResponse {
  analyses?: SocialListeningRun[] | null;
}

interface SocialListeningDetailsResponse {
  data: SocialListeningRunDetails;
}

type SocialListeningRequestBody =
  | SocialListeningBrandAnalysisPayload
  | SocialListeningCompetitiveAnalysisPayload
  | { user_id: number }
  | { user_id: number; run_id: number };

const request = async <TResponse,>(
  path: string,
  method: "POST" | "DELETE",
  body: SocialListeningRequestBody
): Promise<TResponse> => {
  const params = new URLSearchParams({ lang: normalizeApiLang(getApiLangFromI18n(i18n)) });
  const response = await fetch(`${SOCIAL_LISTENING_URL}/${path}?${params.toString()}`, {
    method,
    headers: { ...buildAuthHeaders("api_key"), Accept: "application/json" },
    body: JSON.stringify(body),
  });
  return handleJsonResponse<TResponse>(response);
};

export const startSocialListeningBrandAnalysis = (payload: SocialListeningBrandAnalysisPayload) =>
  request<SocialListeningStartResponse>("brand-analysis", "POST", payload);

export const startSocialListeningCompetitiveAnalysis = (
  payload: SocialListeningCompetitiveAnalysisPayload
) => request<SocialListeningStartResponse>("competitive-analysis", "POST", payload);

export const fetchSocialListeningRuns = async (userId: number): Promise<SocialListeningRun[]> => {
  const response = await request<SocialListeningListResponse>("analyses-list", "POST", {
    user_id: userId,
  });
  return response.analyses ?? [];
};

export const fetchSocialListeningRunDetails = async (
  userId: number,
  runId: number
): Promise<SocialListeningRunDetails> => {
  const response = await request<SocialListeningDetailsResponse>("analysis-details", "POST", {
    user_id: userId,
    run_id: runId,
  });
  return response.data;
};

/** Deletes the whole run. Only the user who started it may delete it; anyone else gets a 404. */
export const deleteSocialListeningRun = async (userId: number, runId: number): Promise<void> => {
  await request<unknown>("delete-analysis", "DELETE", { user_id: userId, run_id: runId });
};
